/**
 * LenderNetworkSelection - POLISHED VERSION
 * Select lenders to receive your application
 */
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '../../ui/components';
import { ShieldCheck, ArrowLeft, ArrowRight } from 'lucide-react';
import { setCurrentStep } from '../../store/loanApplicationSlice';
import ApplyLayout from '../layout/ApplyLayout';

const LenderNetworkSelection = () => {
  const dispatch = useDispatch();
  const [selectedLenders, setSelectedLenders] = useState([]);

  const lenders = [
    { id: 'all', name: 'All Qualified Lenders', description: 'Maximum exposure to get the best rates', recommended: true },
    { id: 'top-5', name: 'Top 5 Lenders', description: 'Best-rated lenders based on your profile' },
    { id: 'local', name: 'Local Lenders Only', description: 'Lenders in your area' },
    { id: 'custom', name: 'Custom Selection', description: 'Choose specific lenders' },
  ];

  const handleContinue = () => {
    dispatch(setCurrentStep(5)); // Lender submission
  };

  const handleBack = () => {
    dispatch(setCurrentStep(3.7)); // Back to underwriting
  };

  const handleSelectionChange = (value) => {
    setSelectedLenders([value]);
  };

  return (
    <ApplyLayout
      step={5}
      totalSteps={9}
      title="Choose Your Lender Network"
      subtitle="Select which lenders will receive your application"
      context="lender-selection"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
        {lenders.map((lender) => {
          const isSelected = selectedLenders.includes(lender.id);
          
          return (
            <div
              key={lender.id}
              onClick={() => handleSelectionChange(lender.id)}
              style={{
                backgroundColor: isSelected ? 'var(--lk-primary-50)' : 'var(--lk-bg-surface-1)',
                border: isSelected ? '2px solid var(--lk-primary-500)' : '1px solid var(--lk-border)',
                borderRadius: 'var(--lk-radius-lg)',
                padding: 'var(--lk-space-5)',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all var(--lk-duration-fast) var(--lk-ease)',
              }}
            >
              {lender.recommended && (
                <div style={{
                  position: 'absolute', top: -10, right: 20,
                  backgroundColor: 'var(--lk-success-600)', color: 'white',
                  padding: '4px 12px', borderRadius: 12,
                  fontSize: 'var(--lk-text-xs)', fontWeight: 600,
                }}>
                  RECOMMENDED
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)' }}>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                  border: isSelected ? '6px solid var(--lk-primary-500)' : '2px solid var(--lk-border)',
                  transition: 'border var(--lk-duration-fast) var(--lk-ease)',
                }} />

                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 'var(--lk-text-md)', fontWeight: isSelected ? 600 : 500, marginBottom: 4 }}>
                    {lender.name}
                  </div>
                  <div style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>
                    {lender.description}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Box */}
      <div style={{
        marginTop: 'var(--lk-space-8)',
        padding: 'var(--lk-space-5)',
        backgroundColor: 'var(--lk-bg-surface-2)',
        borderRadius: 'var(--lk-radius-lg)',
        border: '1px solid var(--lk-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--lk-space-2)' }}>
          <ShieldCheck size={20} color="var(--lk-success-500)" style={{ marginTop: 2, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 'var(--lk-text-sm)', fontWeight: 500, marginBottom: 4 }}>
              Your Information is Protected
            </div>
            <p style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)', margin: 0 }}>
              Lenders will receive your application anonymously. Your personal contact information 
              is only shared with lenders you choose to work with.
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 'var(--lk-space-3)', marginTop: 'var(--lk-space-10)' }}>
        <Button variant="secondary" size="lg" icon={ArrowLeft} onClick={handleBack} style={{ minWidth: 120 }}>
          Back
        </Button>
        <Button variant="primary" size="lg" icon={ArrowRight} onClick={handleContinue} disabled={selectedLenders.length === 0} style={{ flex: 1 }}>
          Continue to Review
        </Button>
      </div>
    </ApplyLayout>
  );
};

export default LenderNetworkSelection;
